
import type {
  FeatureMenuConfig,
  FeatureNode,
  FeaturesTree,
  NavigationMenuSection,
} from '../../models'
import type { FeatureConfigProvider } from '../../providers/interfaces/feature-config.provider'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'

import { defineComponent } from 'vue'

import { mapActions, mapGetters } from 'vuex'

import { CONTAINER } from '../../config/inversify.config'

import { TYPES } from '../../config/inversify.types'

import { DeviceEnum, FeatureIdEnum } from '../../enums'

import { SteppedAnimationMixin } from '../../mixins/stepped-animation.mixin'

import { ROUTER_CONSTS } from '../../router/router.consts'
import { APP_CONSTS } from '../../store/app/app.consts'
import { CONTEXT_CONSTS } from '../../store/context/context.consts'
import { FeaturesTreeUtils } from '../../utils'
import { EpbButton } from '../shared/epb-button'
import NavigationMenuFooter from './navigation-menu-footer.component.vue'
import NavigationMenuHeader from './navigation-menu-header.component.vue'
import NavigationMenuItem from './navigation-menu-item.component.vue'

export enum MenuSectionEnum {
  HEADER,
  BODY,
  FOOTER,
}

export default defineComponent({
  name: 'NavigationMenu',
  components: {
    EpbButton,
    NavigationMenuHeader,
    NavigationMenuItem,
    NavigationMenuFooter,
  },
  mixins: [SteppedAnimationMixin],
  props: {
    collapsible: Boolean,
    opened: Boolean,
  },
  data(): {
    selectedSectionId?: string
    hovered: boolean
    subMenuId: string
    nextSubMenuId: string
    timeout: number
    animationFinished: boolean
    MenuSectionEnum
  } {
    return {
      selectedSectionId: undefined,
      hovered: false,
      subMenuId: undefined,
      nextSubMenuId: undefined,
      timeout: undefined,
      animationFinished: false,
      MenuSectionEnum,
    }
  },
  computed: {
    ...mapGetters(CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
    }),
    ...mapGetters(APP_CONSTS.moduleName, {
      featuresTree: APP_CONSTS.getters.featuresTree,
      collapsedInState: APP_CONSTS.getters.menuCollapsed,
    }),
    parentFeatureNodes() {
      let parents = []

      Object.values(this.featuresTree as FeaturesTree).forEach(values => {
        parents = [...parents, ...values.map(({ id }) => id)]
      })
      return parents
    },
    collapsed(): boolean {
      return this.collapsible && this.collapsedInState
    },
    sections(): NavigationMenuSection[] {
      const sections = Object.keys(this.featuresTree).reduce(
        (result: NavigationMenuSection[], domain: DomainEnum) => {
          if (domain !== DomainEnum.GLOBAL && !!this.featuresTree[domain].length) {
            result.push({
              id: domain,
              label: this.$epb.messages.app.domains[domain] ?? domain,
              icon: {
                [DomainEnum.SEARCH]: 'searchdomain',
                [DomainEnum.BROWSE]: 'discoverydomain',
              }[domain],
            })
          }
          return result
        },
        [],
      )
      return sections
    },
    collapsibleHeader(): boolean {
      return this.collapsible && this.hovered && this.animationFinished
    },
    isMobileDevice(): boolean {
      return this.$epb.device === DeviceEnum.MOBILE
    },
  },
  methods: {
    ...mapActions(APP_CONSTS.moduleName, {
      setMenuCollapsed: APP_CONSTS.actions.setMenuCollapsed,
    }),
    onMouseOver(): void {
      if (this.animationFinished) {
        this.hovered = true
      }
    },
    onMouseLeave(): void {
      this.hovered = false
    },
    getItemFromRef(id: string): unknown {
      return this.$refs.menuItemRef.find(itemRef => itemRef.id === id)
    },
    itemMouseOver(id: string): void {
      const item = this.getItemFromRef(id)
      if (!item.children.length) {
        return
      }

      if (id === this.subMenuId) {
        this.nextSubMenuId = undefined
        this.resetTimeout()
        return
      }

      if (this.subMenuId) {
        this.nextSubMenuId = id
        return
      }
      this.subMenuId = id
    },
    itemMouseSelect(): void {
      this.resetTimeout()
      this.subMenuId = undefined
      this.nextSubMenuId = undefined
    },
    itemMouseLeave(id: string): void {
      const item = this.getItemFromRef(id)
      if (!item.children.length) {
        return
      }

      this.resetTimeout()
      this.nextSubMenuId = undefined

      this.timeout = setTimeout(() => {
        this.subMenuId = this.nextSubMenuId
        this.timeout = undefined
      }, 400)
    },
    subItemMouseEnter(): void {
      this.resetTimeout()
      this.nextSubMenuId = undefined
    },
    subItemMouseLeave(): void {
      this.subMenuId = undefined
    },
    resetTimeout(): void {
      if (this.timeout) {
        clearTimeout(this.timeout)
        this.timeout = undefined
      }
    },
    updateCollapsedInState(collapsed: boolean): void {
      if (collapsed) {
        this.hovered = false
      }
      this.setMenuCollapsed(collapsed)
    },
    getMenuItems(section: MenuSectionEnum): any[] {
      const menuItems = []
      const { tree: filteredFeaturesTree, totalItems } = this.getFilteredFeaturesTree(section)
      let indexOfMenuItem = 0
      Object.keys(filteredFeaturesTree).forEach((domain: DomainEnum) => {
        const featureNodes = filteredFeaturesTree[domain]
        featureNodes.forEach((featureNode: FeatureNode) => {
          const { item: menuItem, index: newIndexOfMenuItem } = this.getMenuItem(
            featureNode,
            domain,
            section,
            indexOfMenuItem,
            totalItems,
          )
          menuItems.push(menuItem)
          indexOfMenuItem = newIndexOfMenuItem
        })
      })
      menuItems.sort((item1: FeatureNode, item2: FeatureNode) =>
        item1.id === FeatureIdEnum.HIGHLIGHTS ? -1 : item2.id === FeatureIdEnum.HIGHLIGHTS ? 1 : 0,
      )
      menuItems.forEach((menuItem: any, index: number) => {
        this.addTransitionToMenuItem(menuItem, section, index, totalItems)
      })
      return menuItems
    },
    getFilteredFeaturesTree(section: MenuSectionEnum) {
      const filteredFeaturesTree = {}
      let totalItems = 0
      Object.keys(this.featuresTree).forEach((domain: DomainEnum) => {
        if (domain === DomainEnum.SEARCH || domain === DomainEnum.GLOBAL) {
          const featureNodes = this.featuresTree[domain]

          const { nodes: domainNodes, totalItems: domainTotalItems } = featureNodes.reduce(
            (accum, featureNode: FeatureNode) => {
              if (this.isFeatureValidToAdd(section, featureNode.id, featureNode.menuConfig)) {
                accum.nodes.push(featureNode)
                accum.totalItems += this.getFeatureNodeTotalItems(featureNode)
              }
              return accum
            },
            { nodes: [], totalItems: 0 },
          )
          filteredFeaturesTree[domain] = domainNodes
          totalItems += domainTotalItems
        }
      })
      return { tree: filteredFeaturesTree, totalItems }
    },
    isFeatureValidToAdd(
      section: MenuSectionEnum,
      id: FeatureIdEnum,
      menuConfig: FeatureMenuConfig,
    ): boolean {
      if (menuConfig?.hidden) {
        return false
      }
      if (section === MenuSectionEnum.HEADER && menuConfig?.displayOnHeader) {
        return true
      }

      return section === MenuSectionEnum.BODY && !menuConfig?.displayOnHeader
    },
    getFeatureNodeTotalItems(featureNode: FeatureNode): number {
      let totalItems = 1
      if (
        !!featureNode.children &&
        !this.areFeatureChildrenIgnored(featureNode) &&
        !this.areFeatureChildrenDisplayedOnModal(featureNode)
      ) {
        featureNode.children.forEach((childFeatureNode: FeatureNode) => {
          totalItems += this.getFeatureNodeTotalItems(childFeatureNode)
        })
      }
      return totalItems
    },
    areFeatureChildrenIgnored(featureNode: FeatureNode): boolean {
      return !!featureNode.menuConfig && !!featureNode.menuConfig.ignoreChildren
    },
    areFeatureChildrenDisplayedOnModal(featureNode: FeatureNode): boolean {
      return (
        featureNode.menuConfig?.displayChildrenOnModal ||
        (this.collapsed && !!featureNode.children.length && !featureNode.menuConfig?.ignoreChildren)
      )
    },
    getMenuItem(
      featureNode: FeatureNode,
      domain: DomainEnum,
      section: MenuSectionEnum,
      index: number,
      totalItems: number,
    ) {
      const hasIcon = this.parentFeatureNodes.includes(featureNode.id)
      const getFeatureConfigProvider = CONTAINER.get<FeatureConfigProvider>(
        TYPES.FeatureConfigProvider,
      )
      const featureConfig = getFeatureConfigProvider(featureNode.id as FeatureIdEnum, {
        domain:
          featureNode.routeName ===
            ROUTER_CONSTS.routes[FeatureIdEnum.OVERALL][DomainEnum.BROWSE] ||
          featureNode.routeName ===
            ROUTER_CONSTS.routes[FeatureIdEnum.CONFIGURATION][DomainEnum.BROWSE].root
            ? DomainEnum.BROWSE
            : domain,
      })

      const menuItem: any = {
        id: featureNode.id,
        label: featureConfig.name,
        ...(hasIcon && {
          icon: featureConfig.icon,
        }),
        route: this.getFeatureRoute(featureNode),
        selected: this.isFeatureSelected(featureNode, domain),
        isNew: featureConfig.new,
      }
      index++
      if (!!featureNode.children && !this.areFeatureChildrenIgnored(featureNode)) {
        const displayChildrenOnModal = this.areFeatureChildrenDisplayedOnModal(featureNode)
        menuItem.children = featureNode.children.map((childFeatureNode: FeatureNode) => {
          const { item: childMenuItem } = this.getMenuItem(
            childFeatureNode,
            domain,
            section,
            index,
            totalItems,
          )
          return childMenuItem
        })
        menuItem.displayChildrenOnModal = displayChildrenOnModal
      }
      return { item: menuItem, index }
    },
    addTransitionToMenuItem(
      menuItem: any,
      section: MenuSectionEnum,
      index: number,
      totalItems: number,
    ) {
      if (!this.animationFinished) {
        const itemTransitionDurationInMillis = Math.min(
          this.animationStepDurationsInMillis[section] / totalItems,
          200,
        )
        menuItem.transition = {
          durationInMillis: itemTransitionDurationInMillis,
          delayInMillis: itemTransitionDurationInMillis * index,
        }
      }
    },
    getFeatureRoute(featureNode: FeatureNode) {
      if (featureNode.routeName) {
        return {
          ...this.$route,
          name: featureNode.routeName,
        }
      }
    },
    isFeatureIdOnNodeChilds(featureNode: FeatureNode): boolean {
      return (
        !!featureNode.children &&
        featureNode.children.some(
          (childNode: FeatureNode) => childNode.id === this.$route.meta.featureId,
        )
      )
    },
    isFeatureSelected(featureNode: FeatureNode, domain: DomainEnum): boolean {
      if (domain === this.context.domain || domain === DomainEnum.GLOBAL) {
        if (
          featureNode.id === this.$route.meta.featureId ||
          this.isFeatureIdOnNodeChilds(featureNode)
        ) {
          if (
            featureNode.routeName ===
              ROUTER_CONSTS.routes[FeatureIdEnum.OVERALL][DomainEnum.BROWSE] ||
            featureNode.routeName ===
              ROUTER_CONSTS.routes[FeatureIdEnum.CONFIGURATION][DomainEnum.BROWSE].root ||
            featureNode.id === FeatureIdEnum.BROWSE
          ) {
            return false
          }
          return true
        } else if (!!featureNode.children && this.areFeatureChildrenDisplayedOnModal(featureNode)) {
          return !!FeaturesTreeUtils.getFeatureNode(
            this.$route.meta.featureId,
            featureNode.children,
          )
        }
      } else {
        if (
          featureNode.id === this.$route.meta.featureId ||
          this.isFeatureIdOnNodeChilds(featureNode)
        ) {
          if (
            featureNode.routeName ===
              ROUTER_CONSTS.routes[FeatureIdEnum.OVERALL][DomainEnum.SEARCH] ||
            (featureNode.routeName ===
              ROUTER_CONSTS.routes[FeatureIdEnum.CONFIGURATION][DomainEnum.SEARCH].list &&
              featureNode.id !== FeatureIdEnum.INSIGHTS) ||
            featureNode.id === FeatureIdEnum.PLAY
          ) {
            return false
          }
          return true
        }
      }
    },
    onAnimationFinished(): void {
      this.animationFinished = true
    },
  },
  watch: {
    subMenuId: {
      handler(subMenuId: string, prevSubMenuId: string): void {
        if (prevSubMenuId) {
          const prevItem = this.getItemFromRef(prevSubMenuId)
          prevItem.onHover(false)
        }
        if (subMenuId) {
          const item = this.getItemFromRef(subMenuId)
          item.onHover(true)
        }
      },
      deep: true,
    },
    'context.domain': {
      handler(domain: DomainEnum): void {
        if (!!domain && domain !== DomainEnum.GLOBAL) {
          this.selectedSectionId = domain
        }
      },
    },
    sections: {
      handler(sections: NavigationMenuSection[]): void {
        if (!sections.some(({ id }: NavigationMenuSection) => id === this.selectedSectionId)) {
          this.selectedSectionId = sections[0]?.id
        }
      },
      deep: true,
    },
  },
  mounted(): void {
    let selectedSectionId: string
    if (!!this.context.domain && this.context.domain !== DomainEnum.GLOBAL) {
      selectedSectionId = this.context.domain
    } else {
      selectedSectionId = this.sections[0]?.id
    }
    this.selectedSectionId = selectedSectionId
    if (!this.collapsible || this.collapsed) {
      this.currentAnimationStep = MenuSectionEnum.FOOTER
    }
  },
})
